import React from "react";
import { FileText } from "lucide-react";
import CrudManager from "../components/CrudManager";
import { api, resolveMediaUrl } from "../../lib/api";

const GENERAL_INFORMATION = "A. General Information";
const DOCUMENTS_INFORMATION = "B. Documents and Information";
const RESULT_CLASS_X = "C. Result Class X";
const RESULT_CLASS_XII = "D. Result Class XII";
const STAFF_DETAILS = "E. Staff Details";
const SCHOOL_INFRASTRUCTURE = "F. School Infrastructure";
const OTHER_INFORMATION = "G. Other Information";

const categoryOptions = [
  GENERAL_INFORMATION,
  DOCUMENTS_INFORMATION,
  RESULT_CLASS_X,
  RESULT_CLASS_XII,
  STAFF_DETAILS,
  SCHOOL_INFRASTRUCTURE,
  OTHER_INFORMATION,
];

/*
|--------------------------------------------------------------------------
| Text Based Sections
|--------------------------------------------------------------------------
*/

const detailCategories = [
  GENERAL_INFORMATION,
  STAFF_DETAILS,
  SCHOOL_INFRASTRUCTURE,
  OTHER_INFORMATION,
];

/*
|--------------------------------------------------------------------------
| PDF Section
|--------------------------------------------------------------------------
*/

const documentCategories = [
  DOCUMENTS_INFORMATION,
];

/*
|--------------------------------------------------------------------------
| Result Sections
|--------------------------------------------------------------------------
*/

const resultCategories = [
  RESULT_CLASS_X,
  RESULT_CLASS_XII,
];

const fields = [
  {
    name: "category",
    label: "Disclosure Section",
    type: "select",
    options: categoryOptions,
    defaultValue: GENERAL_INFORMATION,
    required: true,
    fullWidth: true,
    help: "Select the mandatory disclosure section.",
  },

  {
    name: "title",
    label: "Information / Document Title",
    placeholder: "Enter information title",
    required: true,
    fullWidth: true,
  },

  /*
  |--------------------------------------------------------------------------
  | General, Staff, Infrastructure and Other Details
  |--------------------------------------------------------------------------
  */

  {
    name: "detail",
    label: "Information Details",
    type: "textarea",
    placeholder: "Enter complete information",
    rows: 4,
    fullWidth: true,
    requiredWhenVisible: true,
    showWhen: {
      field: "category",
      in: detailCategories,
    },
    help:
      "Enter the complete information that will be displayed on the website.",
  },

  /*
  |--------------------------------------------------------------------------
  | Documents and Information PDF
  |--------------------------------------------------------------------------
  */

  {
    name: "document.url",
    label: "Upload PDF Document",
    type: "file",
    accept: ".pdf,application/pdf",
    fullWidth: true,
    requiredOnCreate: true,
    showWhen: {
      field: "category",
      in: documentCategories,
    },
    help:
      "PDF is required only for the Documents and Information section.",
  },

  {
    name: "document.name",
    label: "Document Display Name",
    placeholder: "Example: Affiliation Certificate",
    fullWidth: true,
    requiredWhenVisible: true,
    showWhen: {
      field: "category",
      in: documentCategories,
    },
    help:
      "Enter the name that will appear with the PDF document.",
  },

  /*
  |--------------------------------------------------------------------------
  | Result Information
  |--------------------------------------------------------------------------
  */

  {
    name: "academicYear",
    label: "Academic Year",
    placeholder: "Example: 2025-26",
    requiredWhenVisible: true,
    showWhen: {
      field: "category",
      in: resultCategories,
    },
  },

  {
    name: "registeredStudents",
    label: "Registered Students",
    type: "number",
    min: 0,
    defaultValue: 0,
    showWhen: {
      field: "category",
      in: resultCategories,
    },
  },

  {
    name: "passedStudents",
    label: "Students Passed",
    type: "number",
    min: 0,
    defaultValue: 0,
    showWhen: {
      field: "category",
      in: resultCategories,
    },
  },

  {
    name: "passPercentage",
    label: "Pass Percentage",
    type: "number",
    min: 0,
    max: 100,
    step: "0.01",
    placeholder: "Example: 98.50",
    showWhen: {
      field: "category",
      in: resultCategories,
    },
  },

  {
    name: "remarks",
    label: "Remarks",
    type: "textarea",
    rows: 3,
    placeholder: "Enter result remarks",
    fullWidth: true,
    showWhen: {
      field: "category",
      in: resultCategories,
    },
  },

  /*
  |--------------------------------------------------------------------------
  | Common Fields
  |--------------------------------------------------------------------------
  */

  {
    name: "position",
    label: "Display Position",
    type: "number",
    min: 1,
    defaultValue: 1,
    help: "A lower position number will appear first.",
  },

  {
    name: "isActive",
    label: "Disclosure Status",
    type: "checkbox",
    defaultValue: true,
    help: "Show this record on the website.",
  },
];

function getDescription(item) {
  if (detailCategories.includes(item.category)) {
    return item.detail?.trim() || "No information added";
  }

  if (resultCategories.includes(item.category)) {
    const details = [
      item.academicYear
        ? `Session: ${item.academicYear}`
        : null,

      item.registeredStudents !== undefined
        ? `Registered: ${item.registeredStudents}`
        : null,

      item.passedStudents !== undefined
        ? `Passed: ${item.passedStudents}`
        : null,

      item.passPercentage !== undefined &&
      item.passPercentage !== null &&
      item.passPercentage !== ""
        ? `Pass: ${item.passPercentage}%`
        : null,
    ].filter(Boolean);

    return details.join(" • ") || "No result details added";
  }

  if (item.category === DOCUMENTS_INFORMATION) {
    return (
      item.document?.name ||
      item.document?.url?.split("/").pop() ||
      "No PDF document uploaded"
    );
  }

  return "No information added";
}

export default function MandatoryDisclosures() {
  const columns = [
    {
      label: "Information",
      render: (item) => (
        <div className="admin-name-cell">
          <FileText size={20} />

          <div>
            <strong>
              {item.title || "Untitled Disclosure"}
            </strong>

            <small>{getDescription(item)}</small>
          </div>
        </div>
      ),
    },

    {
      label: "Disclosure Section",
      render: (item) => (
        <span className="admin-badge muted">
          {item.category || "Uncategorized"}
        </span>
      ),
    },

    {
      label: "PDF Document",
      render: (item) => {
        if (item.category !== DOCUMENTS_INFORMATION) {
          return (
            <span className="admin-badge muted">
              Not Required
            </span>
          );
        }

        return item.document?.url ? (
          <a
            href={resolveMediaUrl(item.document.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="admin-file-link"
          >
            <FileText size={15} />
            View PDF
          </a>
        ) : (
          <span className="admin-badge muted">
            Not Uploaded
          </span>
        );
      },
    },

    {
      label: "Position",
      accessor: "position",
    },

    {
      label: "Status",
      render: (item) => (
        <span
          className={`admin-badge ${
            item.isActive ? "success" : "muted"
          }`}
        >
          {item.isActive ? "Active" : "Hidden"}
        </span>
      ),
    },
  ];

  return (
    <CrudManager
      title="Mandatory Public Disclosures"
      singular="Disclosure"
      description="Manage general information, PDF documents, academic results, staff details and school infrastructure."
      fields={fields}
      apiResource={api.mandatoryDisclosures}
      columns={columns}
      modalClassName="admin-modal-wide"
    />
  );
}